'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import {
  Alert,
  DatePicker,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
} from '@workspace/ui';
import {
  camelToSnakeCase,
  DATE_RANGE_TRANSFORM_SELF,
  DATE_RANGE_TRANSFORM_UNITS,
  type DateConstraintContext,
  type DateResolvableValue,
  DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS,
  DYNAMIC_FIELD_TYPES,
  dynamicFieldCalculate,
  formatReadOnlyDateValue,
  getCountryFieldNotes,
  getCountryFieldTooltip,
  getCountryFieldScriptRule,
  getDefaultSelectedMonth,
  getDisabledDate,
  isSameDay,
  normalizeDate,
  resolveFieldConstraints,
  resolveToDate,
  shouldShowField,
} from '@workspace/ui/lib';
import type { CountryField, CountryFieldNote as CountryFieldNoteConfig } from '@workspace/ui/types';
import { useEffect, useMemo } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

type DynamicFieldValue = string | Date | string[] | null | undefined;

const hasFieldValue = (type: string, rawValue: DynamicFieldValue) => {
  switch (type) {
    case DYNAMIC_FIELD_TYPES.DATE:
      if (rawValue instanceof Date) return !Number.isNaN(rawValue.getTime());
      if (typeof rawValue === 'string') return rawValue.trim() !== '';
      return false;
    case DYNAMIC_FIELD_TYPES.SELECT:
      return Array.isArray(rawValue)
        ? rawValue.length > 0
        : typeof rawValue === 'string' && rawValue.trim() !== '';
    default:
      if (typeof rawValue === 'string') return rawValue.trim() !== '';
      if (rawValue instanceof Date) return !Number.isNaN(rawValue.getTime());
      if (Array.isArray(rawValue)) return rawValue.length > 0;
      return false;
  }
};

// Clears a conditionally-required field's value once its condition is no longer met (the
// field is hidden), so stale input isn't submitted. Renders nothing — it only runs the
// clearing effect while the field is unmounted from the form UI.
const ClearHiddenField = ({
  countryField,
  form,
}: {
  countryField: CountryField;
  form: UseFormReturn;
}) => {
  const { name, type } = countryField;

  useEffect(() => {
    const currentValue = form.getValues(name) as DynamicFieldValue;
    if (!hasFieldValue(type, currentValue)) return;

    form.setValue(name, type === DYNAMIC_FIELD_TYPES.DATE ? undefined : '', {
      shouldDirty: true,
      shouldValidate: false,
    });
  }, [name, type, form]);

  return null;
};

interface CountryFieldNoteProps<T extends FieldValues> {
  fieldNotes?: CountryFieldNoteConfig[];
  field: string;
  form: UseFormReturn<T>;
  className?: string;
}

const CountryFieldNote = <T extends FieldValues>({
  fieldNotes,
  field,
  form,
  className,
}: CountryFieldNoteProps<T>) => {
  // `UseFormReturn<T>` is invariant in `T`, so it can't be passed where a plain
  // `UseFormReturn` (i.e. `UseFormReturn<FieldValues>`) is expected. Field names here are
  // dynamic runtime strings, so we narrow to the generic form shape at this single boundary.
  const formApi = form as object as UseFormReturn;
  const visibleFieldNotes = getCountryFieldNotes(fieldNotes, field, formApi);
  if (visibleFieldNotes.length === 0) return null;

  return (
    <>
      {visibleFieldNotes.map((fieldNote, index) => (
        <FormDescription key={`${field}-${index}`} className={className}>
          {fieldNote.note}
        </FormDescription>
      ))}
    </>
  );
};

interface ResolveEffectiveDependantMaxDateProps {
  dependantMaxDate?: Date;
  maxMonths?: number;
  startRefValue?: Date;
}

function resolveEffectiveDependantMaxDate({
  dependantMaxDate,
  maxMonths,
  startRefValue,
}: ResolveEffectiveDependantMaxDateProps) {
  if (!startRefValue || maxMonths === undefined) return dependantMaxDate;

  const maxBoundaryDate = new Date(startRefValue);
  const originalDay = maxBoundaryDate.getDate();

  maxBoundaryDate.setHours(0, 0, 0, 0); // normalize to start of day to avoid time-of-day drift
  maxBoundaryDate.setDate(1);
  maxBoundaryDate.setMonth(maxBoundaryDate.getMonth() + maxMonths + 1, 0);
  maxBoundaryDate.setDate(Math.min(originalDay, maxBoundaryDate.getDate()));
  maxBoundaryDate.setDate(maxBoundaryDate.getDate() - 1);

  if (!dependantMaxDate) return maxBoundaryDate;

  return dependantMaxDate < maxBoundaryDate ? dependantMaxDate : maxBoundaryDate;
}

// ---------- DynamicDateField ----------

const DynamicDateField = ({
  countryField,
  form,
  readOnly,
  resolveRefDate,
  placeholder,
  skipNamedRulesForFields,
  fieldNotes,
  allowsRetroactiveEmployment,
  startDateDisclaimer,
}: {
  countryField: CountryField;
  form: UseFormReturn;
  readOnly: boolean;
  resolveRefDate: (value: DateResolvableValue) => Date | undefined;
  placeholder?: string;
  skipNamedRulesForFields?: string[];
  fieldNotes?: CountryFieldNoteConfig[];
  allowsRetroactiveEmployment?: boolean;
  startDateDisclaimer?: string;
}) => {
  const {
    name,
    label,
    min_months,
    max_months,
    start_date_ref,
    conditional_depends,
    calculation,
    date_range_transform,
    disabled_dates: disabledDatesConfig,
    override = false,
  } = countryField;

  const tooltipNote = getCountryFieldTooltip(fieldNotes, name, form);

  // eor-app naming support - unfortunate case normalisation, but unavoidable for overrides.
  // TODO: remove this once eor-app is updated to use snake_case field names
  const normalizedSnakeCaseName = override ? camelToSnakeCase(name) : name;
  const isStartDate = normalizedSnakeCaseName === 'start_date';
  const isEndDate = normalizedSnakeCaseName === 'end_date';
  const startRefValue = start_date_ref ? resolveRefDate(form.watch(start_date_ref)) : undefined;

  // Watch the range-transform reference date independently of start_date_ref so that
  // the hydrate effect below can fire even when start_date_ref is absent.
  const rangeFromFieldName = date_range_transform?.from_field;
  const rangeFromValue = rangeFromFieldName
    ? resolveRefDate(form.watch(rangeFromFieldName))
    : undefined;

  const conditionalFields = conditional_depends?.fields ?? [];
  const conditionalFromField = conditionalFields[0] ? form.watch(conditionalFields[0]) : undefined;
  const conditionalToField = conditionalFields[1] ? form.watch(conditionalFields[1]) : undefined;

  const { dependantMaxDate } = resolveFieldConstraints(
    countryField,
    [conditionalFromField, conditionalToField],
    resolveRefDate
  );

  const calculatedDate =
    startRefValue && calculation ? dynamicFieldCalculate(startRefValue, calculation) : undefined;

  // Equal min/max months means an exact duration (single valid date). The end_date-specific
  // max boundary lands a day before the min boundary and disables everything, so route these
  // through the generic maxMonths path instead.
  const isExactDuration =
    min_months !== undefined && max_months !== undefined && min_months === max_months;
  const useEndDateMaxBoundary = isEndDate && !isExactDuration;

  const constraintContext: DateConstraintContext = useMemo(() => {
    const effectiveDependantMaxDate = useEndDateMaxBoundary
      ? resolveEffectiveDependantMaxDate({
          dependantMaxDate,
          maxMonths: max_months,
          startRefValue,
        })
      : dependantMaxDate;

    return {
      startDate: startRefValue,
      minMonths: min_months,
      maxMonths: useEndDateMaxBoundary ? undefined : max_months,
      dependantMaxDate: effectiveDependantMaxDate,
      allowsRetroactiveEmployment,
    };
  }, [
    dependantMaxDate,
    useEndDateMaxBoundary,
    max_months,
    min_months,
    startRefValue,
    allowsRetroactiveEmployment,
  ]);

  const defaultMonth = useMemo(
    () => getDefaultSelectedMonth(constraintContext, disabledDatesConfig),
    [constraintContext, disabledDatesConfig]
  );
  const disabledDates = useMemo(
    () =>
      getDisabledDate(normalizedSnakeCaseName, constraintContext, {
        skipNamedRulesForFields,
        disabledDates: disabledDatesConfig,
      }),
    [normalizedSnakeCaseName, constraintContext, skipNamedRulesForFields, disabledDatesConfig]
  );

  // Disable the picker entirely when a required date reference is missing.
  const isFieldDisabled =
    calculatedDate !== undefined || (start_date_ref !== undefined && startRefValue === undefined);

  // Clear the current value whenever constraints change and it falls outside the valid range.
  useEffect(() => {
    if (readOnly || !disabledDates) return;

    const currentValue = form.getValues(name);

    if (!(currentValue instanceof Date) || Number.isNaN(currentValue.getTime())) return;

    if (disabledDates(currentValue)) {
      form.setValue(name, undefined, { shouldValidate: true });
    }
  }, [disabledDates, name, form, readOnly]);

  // prefill date field if calculation option is present
  useEffect(() => {
    if (!calculatedDate) return;

    const currentValue = resolveRefDate(form.getValues(name));
    if (currentValue && isSameDay(currentValue, calculatedDate)) return;

    form.setValue(name, calculatedDate, { shouldValidate: true });
  }, [calculatedDate, form, name, resolveRefDate]);

  // Hydrate a numeric existing value (e.g. probation_period_in_days = 45) back to a
  // Date once the reference date becomes available. Only applies to "self" transforms —
  // the picker shows this field's own end-date and the numeric value is the day/month count
  // from the reference date stored by the backend.
  useEffect(() => {
    if (!date_range_transform || date_range_transform.to_field !== DATE_RANGE_TRANSFORM_SELF)
      return;
    if (!rangeFromValue) return;

    const rawValue = form.getValues(name);

    // Skip if already a valid Date (picker was interacted with or already hydrated).
    if (rawValue instanceof Date && !Number.isNaN(rawValue.getTime())) return;

    const numericCount = typeof rawValue === 'string' ? Number(rawValue) : NaN;
    if (Number.isNaN(numericCount)) return;

    const hydratedDate = new Date(rangeFromValue);
    if (date_range_transform.unit === DATE_RANGE_TRANSFORM_UNITS.DAYS) {
      hydratedDate.setDate(hydratedDate.getDate() + numericCount);
    } else if (date_range_transform.unit === DATE_RANGE_TRANSFORM_UNITS.MONTHS) {
      hydratedDate.setMonth(hydratedDate.getMonth() + numericCount);
    }

    form.setValue(name, normalizeDate(hydratedDate), { shouldValidate: true });
  }, [rangeFromValue, name, form, date_range_transform]);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel tooltip={tooltipNote?.note}>{label}</FormLabel>
          <FormControl>
            {readOnly ? (
              <Input
                value={formatReadOnlyDateValue(field.value)}
                placeholder={placeholder}
                readOnly
                parentClassName="w-full"
              />
            ) : (
              <DatePicker
                {...field}
                onBlur={field.onBlur}
                placeholder={placeholder}
                disabled={isFieldDisabled}
                defaultMonth={defaultMonth}
                disabledDates={disabledDates}
              />
            )}
          </FormControl>
          <FormMessage />
          {isStartDate && startDateDisclaimer && (
            <FormDescription>{startDateDisclaimer}</FormDescription>
          )}
          <CountryFieldNote field={name} fieldNotes={fieldNotes} form={form} />
        </FormItem>
      )}
    />
  );
};

// ---------- DynamicSelectField ----------

const DynamicSelectField = ({
  countryField,
  form,
  readOnly,
  resolveRefDate,
  placeholder,
  fieldNotes,
}: {
  countryField: CountryField;
  form: UseFormReturn;
  readOnly: boolean;
  resolveRefDate: (value: DateResolvableValue) => Date | undefined;
  placeholder?: string;
  fieldNotes?: CountryFieldNoteConfig[];
}) => {
  const conditionalFields = countryField.conditional_depends?.fields ?? [];
  const conditionalFromField = conditionalFields[0] ? form.watch(conditionalFields[0]) : undefined;
  const conditionalToField = conditionalFields[1] ? form.watch(conditionalFields[1]) : undefined;

  const tooltipNote = getCountryFieldTooltip(fieldNotes, countryField.name, form);

  const allOptions = useMemo(
    () =>
      Object.entries(countryField.options ?? {}).map(([key, value]) => ({
        name: String(value),
        value: key,
      })),
    [countryField.options]
  );

  const { allowedOptionKeys } = resolveFieldConstraints(
    countryField,
    [conditionalFromField, conditionalToField],
    resolveRefDate
  );

  const options = useMemo(() => {
    if (!allowedOptionKeys) return allOptions;
    const allowedSet = new Set(allowedOptionKeys);
    return allOptions.filter((opt) => allowedSet.has(opt.value));
  }, [allOptions, allowedOptionKeys]);

  const currentValue = form.watch(countryField.name) as string | undefined;

  useEffect(() => {
    const defaultValue = countryField.default_value;
    if (defaultValue === undefined || defaultValue === null) return;
    if (typeof currentValue === 'string' && currentValue.trim() !== '') return;

    form.setValue(countryField.name, String(defaultValue), { shouldValidate: true });
  }, [countryField.default_value, countryField.name, currentValue, form]);

  useEffect(() => {
    if (readOnly) return;
    if (!currentValue) return;
    if (!options.some((opt) => opt.value === currentValue)) {
      form.setValue(countryField.name, '');
    }
  }, [options, currentValue, form, countryField.name, readOnly]);

  const selectedOptionName = options.find((option) => option.value === currentValue)?.name;

  return (
    <FormField
      control={form.control}
      name={countryField.name}
      render={({ field }) => (
        <FormItem>
          <FormLabel tooltip={tooltipNote?.note}>{countryField.label}</FormLabel>
          <FormControl>
            {readOnly ? (
              <Input
                value={selectedOptionName ?? ''}
                placeholder={placeholder}
                readOnly
                parentClassName="w-full"
              />
            ) : (
              <Select
                options={options}
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                placeholder={placeholder}
              />
            )}
          </FormControl>
          <FormMessage />
          <CountryFieldNote field={countryField.name} fieldNotes={fieldNotes} form={form} />
        </FormItem>
      )}
    />
  );
};

// ---------- DynamicTextField ----------

const DynamicTextField = ({
  countryField,
  form,
  readOnly,
  resolveRefDate,
  placeholder,
  fieldNotes,
}: {
  countryField: CountryField;
  form: UseFormReturn;
  readOnly: boolean;
  resolveRefDate: (value: DateResolvableValue) => Date | undefined;
  placeholder?: string;
  fieldNotes?: CountryFieldNoteConfig[];
}) => {
  const conditionalFields = countryField.conditional_depends?.fields ?? [];
  const conditionalFromField = conditionalFields[0] ? form.watch(conditionalFields[0]) : undefined;
  const conditionalToField = conditionalFields[1] ? form.watch(conditionalFields[1]) : undefined;

  const tooltipNote = getCountryFieldTooltip(fieldNotes, countryField.name, form);

  const { validationRules } = resolveFieldConstraints(
    countryField,
    [conditionalFromField, conditionalToField],
    resolveRefDate
  );

  // When length validation depends on a sibling value (equals + value_rules) and no rule
  // resolves yet (source not selected, no "default" rule), disable the input until it does.
  const hasEqualsValueRules =
    countryField.conditional_depends?.condition ===
      DYNAMIC_FIELD_CONDITIONAL_DEPENDS_CONDITIONS.EQUALS &&
    !!countryField.conditional_depends.value_rules;
  const isFieldDisabled = hasEqualsValueRules && !validationRules;

  const maxLength = validationRules?.max_length ?? countryField.max_length;
  const scriptRule = getCountryFieldScriptRule(countryField.script);
  const inputPlaceholder = countryField.placeholder || placeholder;

  useEffect(() => {
    if (readOnly) return;
    if (!isFieldDisabled) return;

    const currentValue = form.getValues(countryField.name) as DynamicFieldValue;
    if (!hasFieldValue(countryField.type, currentValue)) return;

    form.setValue(countryField.name, '');
  }, [isFieldDisabled, countryField.name, countryField.type, form, readOnly]);

  return (
    <FormField
      control={form.control}
      name={countryField.name}
      render={({ field }) => (
        <FormItem>
          <FormLabel tooltip={tooltipNote?.note}>{countryField.label}</FormLabel>
          <FormControl>
            {readOnly ? (
              <Input
                value={field.value as string}
                placeholder={inputPlaceholder}
                readOnly
                parentClassName="w-full"
              />
            ) : (
              <Input
                {...field}
                type={countryField.email ? 'email' : 'text'}
                value={field.value as string}
                placeholder={inputPlaceholder}
                maxLength={maxLength}
                min={
                  countryField.digits && typeof countryField.min_value === 'number'
                    ? countryField.min_value
                    : undefined
                }
                max={
                  countryField.digits && typeof countryField.max_value === 'number'
                    ? countryField.max_value
                    : undefined
                }
                alphanumericOnly={!scriptRule && countryField.digits && countryField.letters}
                lettersOnly={!scriptRule && countryField.letters && !countryField.digits}
                noSpaces={
                  (!scriptRule && countryField.letters && !countryField.spaces_allowed) ||
                  !!countryField.email
                }
                numberOnly={!scriptRule && countryField.digits && !countryField.letters}
                capitalizeFirstLetter={!countryField.email && !scriptRule}
                disabled={isFieldDisabled}
                onChange={
                  countryField.uppercase && !countryField.email && !scriptRule
                    ? (event) => field.onChange(event.target.value.toUpperCase())
                    : field.onChange
                }
              />
            )}
          </FormControl>
          <FormMessage />
          <CountryFieldNote field={countryField.name} fieldNotes={fieldNotes} form={form} />
        </FormItem>
      )}
    />
  );
};

// ---------- DynamicCountryFields ----------

interface CountryClassificationNoticeProps {
  advancementLevelLabel: string;
  note: string;
}

function CountryClassificationNotice({
  advancementLevelLabel,
  note,
}: CountryClassificationNoticeProps) {
  return (
    <Alert
      variant="warning"
      description={
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <p>{advancementLevelLabel}</p>
          </li>
          <li>{note}</li>
        </ul>
      }
    />
  );
}

/**
 * Renders a list of dynamic country-specific form fields.
 *
 * @param resolveRefDate - Optional converter from a watched form value to a `Date`.
 *   Defaults to `resolveToDate`, which handles `Date` objects and common date strings.
 *   Override when the form uses a non-standard date string format.
 */

interface DynamicCountryFieldsProps<T extends FieldValues> {
  countryFieldsForCountry: CountryField[];
  form: UseFormReturn<T>;
  readOnly?: boolean;
  resolveRefDate?: (value: DateResolvableValue) => Date | undefined;
  placeholder?: string;
  skipNamedRulesForFields?: string[];
  fieldNotes?: CountryFieldNoteConfig[];
  allowsRetroactiveEmployment?: boolean;
  startDateDisclaimer?: string;
}

const DynamicCountryFields = <T extends FieldValues>({
  countryFieldsForCountry,
  form,
  readOnly = false,
  resolveRefDate,
  placeholder,
  skipNamedRulesForFields,
  fieldNotes,
  allowsRetroactiveEmployment,
  startDateDisclaimer,
}: DynamicCountryFieldsProps<T>) => {
  const resolveDate = resolveRefDate ?? resolveToDate;

  // `UseFormReturn<T>` is invariant in `T`, so it can't be passed where a plain
  // `UseFormReturn` (i.e. `UseFormReturn<FieldValues>`) is expected. Field names here are
  // dynamic runtime strings, so we narrow to the generic form shape at this single boundary
  // and thread it through the internal field components/helpers.
  const formApi = form as object as UseFormReturn;

  return (
    <>
      {countryFieldsForCountry?.length > 0 &&
        countryFieldsForCountry.map((countryField) => {
          const isFieldReadOnly = readOnly || !!countryField.read_only;

          if (!shouldShowField(countryField, formApi)) {
            // Don't mutate values in read-only contexts (e.g. profile view); only clear
            // the stale value in editable forms so it isn't submitted.
            return isFieldReadOnly ? null : (
              <ClearHiddenField
                key={countryField.name}
                countryField={countryField}
                form={formApi}
              />
            );
          }

          if (countryField.type === DYNAMIC_FIELD_TYPES.DATE) {
            return (
              <DynamicDateField
                key={countryField.name}
                countryField={countryField}
                form={formApi}
                readOnly={isFieldReadOnly}
                resolveRefDate={resolveDate}
                placeholder={placeholder}
                skipNamedRulesForFields={skipNamedRulesForFields}
                fieldNotes={fieldNotes}
                allowsRetroactiveEmployment={allowsRetroactiveEmployment}
                startDateDisclaimer={startDateDisclaimer}
              />
            );
          }

          if (
            countryField.type === DYNAMIC_FIELD_TYPES.SELECT &&
            (countryField.options || placeholder)
          ) {
            return (
              <DynamicSelectField
                key={countryField.name}
                countryField={countryField}
                form={formApi}
                readOnly={isFieldReadOnly}
                resolveRefDate={resolveDate}
                placeholder={placeholder}
                fieldNotes={fieldNotes}
              />
            );
          }

          return (
            <DynamicTextField
              key={countryField.name}
              countryField={countryField}
              form={formApi}
              readOnly={isFieldReadOnly}
              resolveRefDate={resolveDate}
              placeholder={placeholder}
              fieldNotes={fieldNotes}
            />
          );
        })}
    </>
  );
};

export { CountryClassificationNotice, CountryFieldNote, DynamicCountryFields, hasFieldValue };
